import { useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import useSound from 'use-sound'

import getWindowSize from '../../utils/hooks/getWindowSize'
import { MAX_CARDS } from '../../utils/constants'
import OptionsContext from '../../utils/OptionsContext'
import antagonistsJson from '../../data/antagonists.json'
import cardsJson from '../../data/cards.json'
import { setCardHand, setPlayFromScenario } from '../../api/storage'
import { getScenarioCards, getAntagonistFromCard } from '../../api/engine'
import { OwlDialogue } from '..'
import CardItem from './CardItem'
import addCardSound from '../../assets/sounds/fx/07-add-card-to-hand.mp3'
import showCardSound from '../../assets/sounds/fx/14-button.mp3'
import styles from './CardList.module.scss'

const WINDOW_SMALL = 768
const WINDOW_800 = 800
const WINDOW_900 = 900
const WINDOW_1700 = 1700

/** isApp variable:  for viewing the card without any buttons at all in android/iOs, and for design variation */

const CardList = ({
  setMyCards,
  myCards,
  allCards,
  setAllCards,
  filter,
  filterType,
  cardSelectable = true,
  isCollectionViewer = false,
  isApp = false,
  onOpenBoost = () => {},
}) => {
  const navigate = useNavigate()
  const [activeCardId, setActiveCardId] = useState()
  const [cards, setCards] = useState([])
  const [showMaxCardsOwl, setShowMaxCardsOwl] = useState(false)
  const { width } = getWindowSize()

  const {
    isMobile,
    options: { shouldReduceMotion, soundEffectsOn, effectsVolume },
  } = useContext(OptionsContext)

  const [playAddCardSound] = useSound(addCardSound, { volume: effectsVolume })
  const [playOpenCardSound] = useSound(showCardSound, {
    volume: effectsVolume,
  })

  const FULL_HAND = myCards.length === MAX_CARDS

  useEffect(() => {
    setCards(allCards)
  }, [allCards])

  useEffect(() => {
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  })

  function handleClick(e) {
    if (e.target.getAttribute('data-click') === 'outside') {
      setActiveCardId(null)
    }
  }

  function handleClickOnCard(card) {
    soundEffectsOn && playOpenCardSound()
    const updatedCards = cards.map((c) =>
      c.id === card.id
        ? {
            ...c,
            isNewCard: false,
          }
        : c
    )

    setCards(updatedCards)

    if (activeCardId === card.id) {
      setActiveCardId(null)
    } else {
      setActiveCardId(card.id)
    }
  }

  function playCardScenario(currentCard) {
    const antagonist = getAntagonistFromCard(antagonistsJson, currentCard)
    if (!antagonist) {
      setActiveCardId(null)
      return
    }
    const cardHand = getScenarioCards(antagonist, cardsJson)
    setPlayFromScenario(true)
    setCardHand(cardHand)
    navigate('/persuade', {
      state: {
        antagonist: antagonist.name,
      },
    })
  }

  function addCardToHand(currentCard) {
    setActiveCardId(null)

    if (FULL_HAND) {
      setShowMaxCardsOwl(true)
      return
    }

    soundEffectsOn && playAddCardSound()

    setMyCards([...myCards, currentCard])
    const newCards = cards.filter((card) => card.id !== currentCard.id)

    setAllCards(newCards)
  }

  const renderEmptyCard = (card) => {
    return (
      <li key={card.id}>
        <div
          className={styles.emptyCard}
          style={{ border: `2px dashed ${card.color}` }}
        ></div>
      </li>
    )
  }

  function renderSize(active) {
    if (active && width <= WINDOW_SMALL) {
      return 'appCard'
    } else if (active && width > WINDOW_SMALL) {
      return 'large'
    } else if (width < WINDOW_SMALL) {
      return 'appCard'
    } else if (width < WINDOW_800) {
      return 'small'
    } else if (width < WINDOW_900) {
      return 'xsmall'
    } else if (width < WINDOW_1700) {
      return 'small'
    } else {
      return 'medium'
    }
  }

  // For the deckbuilder, we need the cardsJson to render empty slots
  const cardsInList = isCollectionViewer ? cards : isApp ? cards : cardsJson

  // sort cardsinList by id
  cardsInList.sort((a, b) =>
    a.id.localeCompare(b.id, undefined, { numeric: true })
  )

  const filteredCards = filter
    ? cardsInList.filter((card) => card[filterType].includes(filter))
    : cardsInList

  return (
    <ul
      className={
        !isMobile
          ? `${styles.cardList} ${
              isCollectionViewer ? styles.collectionViewer : ''
            }`
          : `${styles.cardSingle}`
      }
    >
      <AnimatePresence>
        {showMaxCardsOwl && (
          <motion.div
            className={styles.owlWrapper}
            initial={{ scale: 0 }}
            animate={{
              scale: 1,
              transition: {
                type: shouldReduceMotion ? 'tween' : 'spring',
              },
            }}
            exit={{ scale: 0.5, opacity: 0 }}
          >
            <OwlDialogue
              heading='common:owl:maxcards:heading'
              body='common:owl:maxcards:body'
              setShowOwl={setShowMaxCardsOwl}
            />
          </motion.div>
        )}
      </AnimatePresence>
      {filteredCards.map((card) => {
        const active = activeCardId === card.id
        const collectedCard = cards.find((c) => c.id === card.id)
        return collectedCard ? (
          <CardItem
            key={card.id}
            card={card}
            active={active}
            isApp={isApp}
            cardSelectable={cardSelectable}
            handleClickOnCard={handleClickOnCard}
            isCollectionViewer={isCollectionViewer}
            onOpenBoost={onOpenBoost}
            setActiveCardId={setActiveCardId}
            playCardScenario={playCardScenario}
            addCardToHand={addCardToHand}
            renderSize={renderSize}
          />
        ) : (
          !isMobile && renderEmptyCard(card)
        )
      })}
    </ul>
  )
}

export default CardList
